import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import HeroLive from "@/components/landing/HeroLive";
import HeroSoon from "@/components/landing/HeroSoon";
import HeroPartner from "@/components/landing/HeroPartner";
import { HERO_SLIDES as SLIDES } from "@/config/trails";

const variants = {
  enter: (dir) => ({ opacity: 0, x: dir * 60 }),
  center: { opacity: 1, x: 0 },
  exit: (dir) => ({ opacity: 0, x: dir * -60 }),
};

const pad = (n) => String(n).padStart(2, "0");
const arrowCls = "w-11 h-11 rounded-full border border-line bg-paper grid place-items-center text-ink [@media(hover:hover)]:hover:bg-ink [@media(hover:hover)]:hover:text-parchment active:bg-ink active:text-parchment transition-colors";

const SlideBadge = ({ kind, tr }) => {
  if (kind === "live") return null;
  const label = kind === "partner" ? tr.partner.badge : tr.comingSoon;
  const cls = kind === "partner" ? "bg-rowan/15 text-rowan" : "bg-amber/15 text-amber-dark";
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-extrabold ${cls}`}>{label}</span>;
};

const SliderNav = ({ idx, go }) => {
  const { t } = useLang();
  const tr = t.trails;
  const slide = SLIDES[idx];
  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 mt-16 md:mt-20 flex flex-wrap items-center gap-x-5 gap-y-3" data-testid="hero-slider-nav">
      <button type="button" onClick={() => go(idx - 1, -1)} aria-label={tr.prev} data-testid="hero-prev" className={arrowCls}>
        <ChevronLeft className="w-5 h-5" />
      </button>
      <div className="flex items-center gap-2.5">
        {SLIDES.map((s, i) => (
          <button
            key={s.key}
            type="button"
            aria-current={i === idx ? "true" : undefined}
            aria-label={tr.names[s.key]}
            title={tr.names[s.key]}
            data-testid={`hero-dot-${i}`}
            onClick={() => go(i, i > idx ? 1 : -1)}
            className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${i === idx ? "w-10 bg-moss" : i < idx ? "w-2.5 bg-moss/40 hover:bg-moss/70" : "w-2.5 bg-line hover:bg-moss/50"}`}
          />
        ))}
      </div>
      <button type="button" onClick={() => go(idx + 1, 1)} aria-label={tr.next} data-testid="hero-next" className={arrowCls}>
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="text-sm font-bold text-ink-muted tabular-nums inline-flex flex-wrap items-center gap-2" data-testid="hero-slide-counter">
        <span><span className="text-ink">{pad(idx + 1)}</span> {tr.of} {pad(SLIDES.length)}</span>
        <span className="text-ink">· {tr.names[slide.key]}</span>
        <SlideBadge kind={slide.kind} tr={tr} />
      </div>
    </div>
  );
};

const SlideBody = ({ slide, onPlayNow }) => {
  if (slide.kind === "live") return <HeroLive />;
  if (slide.kind === "partner") return <HeroPartner />;
  return <HeroSoon slide={slide} onPlayNow={onPlayNow} />;
};

export default function Hero() {
  const [[idx, dir], setState] = useState([0, 1]);
  const go = (i, d) => setState([(i + SLIDES.length) % SLIDES.length, d]);
  const slide = SLIDES[idx];
  return (
    <section id="top" data-testid="hero-section" className="relative pt-8 pb-16 md:pt-14 md:pb-20 overflow-hidden">
      <AnimatePresence mode="wait" initial={false} custom={dir}>
        <motion.div
          key={slide.key}
          custom={dir}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.35, ease: "easeOut" }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.12}
          onDragEnd={(_, info) => {
            if (info.offset.x < -90) go(idx + 1, 1);
            else if (info.offset.x > 90) go(idx - 1, -1);
          }}
        >
          <SlideBody slide={slide} onPlayNow={() => go(0, -1)} />
        </motion.div>
      </AnimatePresence>
      <SliderNav idx={idx} go={go} />
    </section>
  );
}
